import React, { useMemo } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import CONFIG from '../../config';
import { getCanonicalLink } from '../../utils/helpers/url';
import { getLocaleFromBrowser } from '../../utils/helpers';
import { Photo } from '../../graphql/graphql';

const { asset_host: assetHost, languages } = CONFIG;

export interface HostSum {
  currentUrl: string;
  protocol: string;
  host: string;
  path: string;
}

export const HeadMeta: React.FC<{
  hostSum: HostSum;
  hideFromRobots?: boolean;
  mainPageImage?: Photo;
}> = ({ hostSum, hideFromRobots, mainPageImage }) => {
  const router = useRouter();
  const { i18n } = useTranslation();
  const [currHostSum, setCurrHostSum] = React.useState(hostSum);

  React.useEffect(() => {
    if ((!hostSum || !hostSum?.path) && typeof window !== 'undefined') {
      const { host: locationHost, protocol } = window.location;

      setCurrHostSum({
        currentUrl: `${protocol}://${locationHost}${router.asPath}`,
        protocol,
        host: locationHost,
        path: router.asPath
      });
    }
  }, [router.asPath]);

  // Determine language from URL instead of singleton i18n to avoid race conditions
  // This is critical for production with high concurrent load
  // Priority: URL path prefix > hostname > i18n.language
  const currentLang = useMemo(() => {
    if (typeof window !== 'undefined') {
      return getLocaleFromBrowser();
    }
    // Fallback to i18n.language only if window is not available (shouldn't happen in this component)
    return i18n.language || CONFIG.defaultLocale;
  }, [i18n.language, router.asPath]);

  const defaultCanonicalLink = useMemo(
    () =>
      hostSum
        ? getCanonicalLink({ ...hostSum, lang: CONFIG.defaultLocale })
        : currHostSum.currentUrl,
    [hostSum, currHostSum]
  );

  const currentCanonicalLink = useMemo(
    () =>
      hostSum
        ? getCanonicalLink({ ...hostSum, lang: currentLang })
        : currHostSum.currentUrl,
    [hostSum, currHostSum, currentLang]
  );

  return (
    <Head>
      <meta
        name="google-site-verification"
        content="lbsPKTikII78xEIjMLTs-ZrNnHmog26F7esxmv_YNuc"
      />
      <meta
        key="viewport"
        content="width=device-width, initial-scale=1.0, minimum-scale=1.0, maximum-scale=2.0, user-scalable=yes, viewport-fit=cover, interactive-widget=resizes-content"
        name="viewport"
      />
      <meta name="google" content="notranslate" />
      <meta
        key="format-detection"
        content="telephone=no"
        name="format-detection"
      />
      <meta key="theme-color" content="#ad7a5b" name="theme-color" />
      {mainPageImage?.full && (
        <>
          <meta property="og:image" content={mainPageImage.full} />
          <meta name="robots" content="max-image-preview:large" />
        </>
      )}
      <link
        href="/static/favicons/favicon-192x192.png"
        rel="apple-touch-icon"
        sizes="192x192"
      />
      <link
        href="/static/favicons/favicon-192x192.png"
        rel="icon"
        sizes="192x192"
        type="image/png"
      />
      <link
        color="#ad7a5b"
        href="/static/favicons/favicon.svg"
        rel="mask-icon"
      />
      <meta
        key="msapplication-config"
        content="/static/browserconfig.xml"
        name="msapplication-config"
      />
      <link href="/static/manifest.webmanifest" rel="manifest" />
      {!hideFromRobots && (
        <>
          {assetHost && <link href={`//${assetHost}`} rel="dns-prefetch" />}
          <link href={currentCanonicalLink} rel="canonical" />
          <link
            rel="alternate"
            hrefLang="x-default"
            href={defaultCanonicalLink}
          />
          {languages.map((lang, key) => (
            <link
              key={key}
              hrefLang={lang}
              rel="alternate"
              href={getCanonicalLink({ ...hostSum, lang })}
            />
          ))}
        </>
      )}
    </Head>
  );
};
